import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logo from '../assets/logo.png';

const WHATSAPP = '447478034690';

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Lee parámetros opcionales que envíe tu payment gateway en la URL de redirección */
function parseReceiptFromUrl(searchParams) {
  const pick = (...keys) => {
    for (const key of keys) {
      const value = searchParams.get(key);
      if (value != null && String(value).trim() !== '') {
        return decodeURIComponent(String(value).trim());
      }
    }
    return null;
  };

  const ref =
    pick('ref', 'reference', 'order', 'order_id', 'orderId', 'transaction_id', 'transactionId') ||
    `TVS-${Date.now().toString(36).toUpperCase().slice(-10)}`;

  return {
    ref,
    planName: pick('plan', 'plan_name', 'product', 'product_name') || 'Suscripción IPTV TVSOLA',
    price: pick('price', 'amount', 'total'),
    duration: pick('duration', 'period'),
    email: pick('email', 'customer_email'),
    screens: pick('screens'),
    confirmedAt: new Date().toISOString(),
  };
}

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();

  const receipt = useMemo(() => parseReceiptFromUrl(searchParams), [searchParams]);

  const whatsappMessage = encodeURIComponent(
    `Hola TVSOLA, acabo de realizar un pedido.\nReferencia: ${receipt.ref}${
      receipt.planName ? `\nPlan: ${receipt.planName}` : ''
    }${receipt.price ? `\nImporte: ${receipt.price}€` : ''}${receipt.email ? `\nEmail: ${receipt.email}` : ''}\nGracias.`,
  );

  const handlePrint = () => window.print();

  return (
    <main className="min-h-screen bg-[#060B1E] text-white py-10 md:py-16 px-4 print:bg-white print:text-black">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10 print:mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400 mb-6 print:border-green-700">
            <svg
              className="w-10 h-10 text-green-400 print:text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2">¡Pago recibido!</h1>
          <p className="text-gray-400 print:text-gray-600 max-w-md mx-auto">
            Su pedido ha sido registrado correctamente. Gracias por confiar en{' '}
            <span className="text-yellow-400 print:text-amber-700 font-semibold">TVSOLA</span>.
          </p>
        </div>

        <article
          id="recibo-pedido"
          className="rounded-3xl border border-yellow-400/25 bg-white/5 backdrop-blur-md overflow-hidden shadow-2xl print:bg-white print:border-gray-300 print:shadow-none"
        >
          <div className="h-1.5 bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-500 print:bg-amber-500" />

          <div className="p-8 md:p-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-8 border-b border-white/10 print:border-gray-200">
              <div className="flex items-center gap-4">
                <img src={logo} alt="TVSOLA" className="w-16 h-auto rounded print:w-14" />
                <div>
                  <p className="font-black text-xl tracking-tight">TVSOLA</p>
                  <p className="text-xs text-gray-400 print:text-gray-500">IPTV Premium · España</p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Recibo de pago</p>
                <p className="font-mono text-lg font-bold text-yellow-400 print:text-amber-800 break-all">
                  {receipt.ref}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-8">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Fecha</p>
                <p className="font-medium">{formatDate(receipt.confirmedAt)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Estado</p>
                <p className="inline-flex items-center gap-2 font-semibold text-green-400 print:text-green-700">
                  <span className="w-2 h-2 rounded-full bg-green-400 print:bg-green-600" />
                  Pago confirmado
                </p>
              </div>
              {receipt.email && (
                <div className="sm:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Email</p>
                  <p className="font-medium break-all">{receipt.email}</p>
                </div>
              )}
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Plan contratado</p>
                <p className="text-xl font-bold">{receipt.planName}</p>
                {receipt.duration && (
                  <p className="text-gray-400 text-sm mt-1 print:text-gray-600">{receipt.duration}</p>
                )}
                {receipt.screens && (
                  <p className="text-yellow-400/90 text-sm mt-1 print:text-amber-700">
                    {receipt.screens} pantalla{Number(receipt.screens) > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            {receipt.price && (
              <div className="rounded-2xl bg-yellow-400/10 border border-yellow-400/20 p-6 mb-8 print:bg-amber-50 print:border-amber-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 print:text-gray-700 font-medium">Total pagado</span>
                  <span className="text-3xl font-black text-yellow-400 print:text-amber-800">
                    {receipt.price}€
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Pago único · Sin permanencia</p>
              </div>
            )}

            <ul className="space-y-2 text-sm text-gray-300 print:text-gray-700 mb-8">
              <li className="flex gap-2">
                <span className="text-yellow-400">✓</span> Reproductor IBO Pro incluido gratis
              </li>
              <li className="flex gap-2">
                <span className="text-yellow-400">✓</span> +79.000 canales y VOD ilimitado
              </li>
              <li className="flex gap-2">
                <span className="text-yellow-400">✓</span> Credenciales enviadas por email en 5–15 min
              </li>
            </ul>

            <p className="text-xs text-gray-500 leading-relaxed">
              Conserve este recibo. Si no recibe sus accesos en 30 minutos, contacte soporte por
              WhatsApp indicando la referencia{' '}
              <strong className="text-gray-400">{receipt.ref}</strong>.
            </p>
          </div>
        </article>

        <div className="mt-10 space-y-4 print:hidden">
          <h2 className="text-lg font-bold text-center mb-4">Próximos pasos</h2>
          <div className="grid sm:grid-cols-3 gap-4 text-center text-sm">
            {[
              { n: '1', t: 'Revise su email', d: 'Busque el mensaje con usuario y contraseña' },
              { n: '2', t: 'Instale la app', d: 'IPTV Smarters o IBO Pro' },
              { n: '3', t: 'Disfrute', d: 'Más de 79.000 canales en 4K' },
            ].map((step) => (
              <div key={step.n} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <span className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-yellow-400 text-black font-bold text-sm mb-2">
                  {step.n}
                </span>
                <p className="font-semibold">{step.t}</p>
                <p className="text-gray-500 text-xs mt-1">{step.d}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <button
              type="button"
              onClick={handlePrint}
              className="border border-white/25 hover:border-yellow-400/50 text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Imprimir recibo
            </button>
            <a
              href={`https://wa.me/${WHATSAPP}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-full text-center transition-colors"
            >
              Soporte WhatsApp
            </a>
            <Link
              to="/"
              className="border border-white/20 text-center text-white font-semibold px-6 py-3 rounded-full hover:bg-white/5"
            >
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          header, footer { display: none !important; }
        }
      `}</style>
    </main>
  );
}
